import { useEffect, useState } from 'react';
import { LocationType } from 'utils/shared/coachBuilder';
import ArrowLeft from 'svg/ArrowLeft';
import ChevronLeft from 'svg/ChevronLeft';
import CloseIcon from 'svg/CloseIcon';
import { BookStepsEnum } from 'screens/PlayerLoggedInScreen/enums';
import {
  BookCoachModalProps,
  BookCoachStepType,
  BookingStateProps,
} from 'screens/PlayerLoggedInScreen/props';
import { ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import ConfirmAndPay from './ConfirmAndPay';
import ConfirmationInfo from './ConfirmationInfo';
import LocationAndTime from './LocationAndTime';
import PickDate from './PickDate';
import PromptUser from './PromptUser';
import SelectType from './SelectType';

const bookingIntialState = {
  lessonType: null,
  dateFromCalendar: null,
  bookedLessonId: '',
  location: {
    id: '',
    title: '',
    address: '',
    locationType: LocationType.Venue,
  },
  currentDateTime: {
    date: new Date(),
    startTime: '',
    endTime: '',
  },
};

const BookCoachModal: React.FC<BookCoachModalProps> = ({
  open,
  handleClose,
  coach,
  refetchUpcomingLessons,
}) => {
  const [currentStep, setCurrentStep] = useState(BookStepsEnum.Prompt);

  const [bookingData, setBookingData] = useState<BookingStateProps>(bookingIntialState);

  const stepSequence = [
    BookStepsEnum.Prompt,
    BookStepsEnum.PickDate,
    BookStepsEnum.LocationTime,
    BookStepsEnum.ConfirmPayment,
    BookStepsEnum.ConfirmationInfo,
  ];

  const handleNext = (data = {}) => {
    setBookingData((prevData) => ({ ...prevData, ...data }));

    const currentIndex = stepSequence.indexOf(currentStep);
    if (currentStep === BookStepsEnum.ConfirmPayment) {
      refetchUpcomingLessons();
    }
    if (currentIndex < stepSequence.length - 1) {
      setCurrentStep(stepSequence[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const currentIndex = stepSequence.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepSequence[currentIndex - 1]);
    }
  };

  const handleReset = () => {
    setCurrentStep(BookStepsEnum.Prompt);
    setBookingData(bookingIntialState);
  };

  const onClose = () => {
    handleClose();
    setTimeout(() => {
      handleReset();
    }, 300);
  };

  const handleBookAnother = () => {
    setCurrentStep(BookStepsEnum.PickDate);
    setBookingData(bookingIntialState);
  };

  const currentStepMapper: Record<BookStepsEnum, BookCoachStepType> = {
    [BookStepsEnum.Prompt]: {
      title: 'Book a lesson',
      allowBack: false,
      render: <PromptUser handleClose={handleClose} coach={coach} onNext={() => handleNext()} />,
    },
    [BookStepsEnum.SelectType]: {
      title: 'Lesson Type',
      allowBack: true,
      render: <SelectType onNext={(lessonType) => handleNext({ lessonType })} />,
    },
    [BookStepsEnum.PickDate]: {
      title: 'Pick a date',
      allowBack: true,
      render: <PickDate onNext={(date) => handleNext({ dateFromCalendar: date })} coach={coach} />,
    },
    [BookStepsEnum.LocationTime]: {
      title: 'Location and Time',
      allowBack: true,
      render: (
        <LocationAndTime
          selectedDate={bookingData.dateFromCalendar as Date}
          coach={coach}
          onNext={handleNext}
        />
      ),
    },
    [BookStepsEnum.ConfirmPayment]: {
      title: 'Confirm and Pay',
      allowBack: true,
      render: <ConfirmAndPay bookingData={bookingData} coach={coach} onNext={handleNext} />,
    },
    [BookStepsEnum.ConfirmationInfo]: {
      title: '',
      allowBack: false,
      render: (
        <ConfirmationInfo
          coach={coach}
          bookingData={bookingData}
          handleBookAnother={handleBookAnother}
        />
      ),
      borderBottom: false,
    },
  };

  const currentStepProps = currentStepMapper[currentStep];

  return (
    <Modal
      classNameMaxWidth="max-w-[40rem]"
      classNameHeight={
        currentStep === BookStepsEnum.LocationTime ? 'h-full max-h-[95dvh]' : 'h-auto'
      }
      className="flex h-full flex-col"
      isOpen={open}
      handleClose={onClose}
    >
      <div
        className={classNames(
          'flex items-center justify-between border-color-border-input-lightmode px-ds-xl py-5 dark:border-color-border-input-darkmode max-sm:px-ds-lg sm:border-b',
          currentStepProps.borderBottom === false && '!border-0',
        )}
      >
        {currentStepProps.allowBack && (
          <ButtonText onClick={handleBack}>
            <ArrowLeft className="h-6 w-6 max-sm:hidden" />
            <ChevronLeft className="h-4 w-4 sm:hidden" />
          </ButtonText>
        )}
        <h3 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile">
          {currentStepProps.title}
        </h3>
        <ButtonText onClick={onClose}>
          <CloseIcon className="h-6 w-6 max-sm:h-4 max-sm:w-4" />
        </ButtonText>
      </div>

      <div className="min-h-0 flex-1">{currentStepProps.render}</div>
    </Modal>
  );
};

export default BookCoachModal;
